
import { Options, prop, Vue } from "vue-class-component";
import SelectGroupV2 from "@/app/ui/components/select-group-v2/index.vue";
import { countries } from "@/app/infrastructures/misc/BulkData";
import maxLengthPhoneNumber from "@/app/infrastructures/misc/common-library/MaxLengthPhoneNumber";
import {
  formatPhoneCustomer,
  isMaskedPhone,
  nationalPrefixExist
} from "@/app/ui/views/shipment/booking/booking-utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import {
  CustomerBookingV3,
  PhoneBookingV3
} from "@/domain/entities/CreateBookingV3";

class Props {
  options = prop<any[]>({
    default: [],
    type: Array
  });
  phone = prop<string>({
    default: "",
    type: String
  });
  code = prop<string>({
    default: "",
    type: String
  });
  error = prop<any>({
    default: {},
    type: Object
  });
  disabled = prop<boolean>({
    default: false,
    type: Boolean
  });
  disabledUserDropdown = prop<boolean>({
    default: false,
    type: Boolean
  });
  column = prop<string>({
    default: "",
    type: String
  });
  customErrorMandatory = prop<string>({
    default: "",
    type: String
  });
  isLoading = prop<boolean>({
    default: false,
    type: Boolean
  });
  keyName = prop<string>({
    default: "name",
    type: String
  });
  phoneNumberFieldId = prop<string>({
    default: ""
  });
  countryCodeButtonId = prop<string>({
    default: ""
  });
  customError = prop<boolean>({
    default: false,
    type: Boolean
  });
  isReverseJourney = prop<boolean>({
    default: false,
    type: Boolean
  });
  useInfiniteScroll = prop<boolean>({
    default: false,
    type: Boolean
  });
  bookingVersion = prop<string>({
    default: "",
    type: String
  });
}

@Options({
  emits: [
    "setErrorPhone",
    "setErrorPhoneCode",
    "update:code",
    "checkFocus",
    "setErrorCharMinimal",
    "update:code",
    "changePhoneNumber",
    "editItem",
    "select",
    "scrolled"
  ],
  components: {
    SelectGroupV2
  }
})
export default class SelectPhoneCountry extends Vue.with(Props) {
  countryFiltered = countries;
  selectedCustomer = new CustomerBookingV3();

  get allCountry() {
    return this.countryFiltered;
  }

  set allCountry(data: Array<any>) {
    this.countryFiltered = data;
  }

  foundDataPhoneCode(value: string) {
    this.allCountry = countries.filter((item: any) => {
      return Object.keys(item).some((key) =>
        item[key].toLowerCase().includes(value.toLowerCase())
      );
    });
  }

  setValueCountry(item: PhoneBookingV3) {
    if (item) {
      this.$emit("setErrorPhoneCode", false);
      this.$emit("update:code", item.code);
      this.$emit("checkFocus");
      this.validatePhone(
        new PhoneBookingV3({
          code: item.code,
          phone: this.phone
        })
      );
    }
  }

  onSelectCode(value: string) {
    this.$emit("update:code", value);
  }

  onFilterCountry(value: string) {
    this.foundDataPhoneCode(value);
    this.checkErrorPhoneCode(value);
  }

  checkErrorPhoneCode(value: string) {
    const codeExist = countries.find((item: any) => item.code === value);

    if (!codeExist) {
      this.$emit("setErrorPhoneCode", true);
    } else {
      this.$emit("setErrorPhoneCode", false);
    }

    this.$emit("update:code", this.code);
  }

  maxLengthPhoneNumber(phone: string) {
    return maxLengthPhoneNumber(phone);
  }

  formatNumber(value: string) {
    if (value.includes("*")) {
      return value;
    }

    const prefix = this.code === "+62" ? /^62|^0|[\D]+/g : /[\D]+/g;
    return value?.replace(prefix, "");
  }

  onSelect(item: CustomerBookingV3) {
    this.$emit("select", formatPhoneCustomer(item));
  }

  onChange(value: string) {
    this.validatePhone(
      new PhoneBookingV3({
        code: this.code,
        phone: value
      })
    );
  }
  validatePhone({ code, phone }: PhoneBookingV3) {
    const codePrefix = code || this.code;
    this.$emit("checkFocus");
    const nationalPrefix = nationalPrefixExist(codePrefix);

    let newValue = this.indonesianPhone(
      new PhoneBookingV3({ phone, code: codePrefix })
    );
    const codeDigits = codePrefix.replace("+", "");
    const regex = new RegExp(`^(${codeDigits}|${nationalPrefix})`);
    newValue = newValue.replace(regex, "");

    if (/^08\d{12,}/.test(newValue)) {
      newValue = newValue.slice(0, -1);
    }

    this.$emit("update:phone", newValue);
    this.$emit("setErrorPhone", newValue);
  }

  get isEditItem() {
    return !AccountController.accountData.isInternalAccount;
  }
  onEditOption(item: CustomerBookingV3) {
    window.open(`/management-customer/edit?id=${item.customerId}`, "_blank");
  }

  pasteText() {
    let newValue;
    const nationalPrefix = nationalPrefixExist(this.code);
    newValue = this.indonesianPhone(
      new PhoneBookingV3({ code: this.code, phone: this.phone })
    );
    if (newValue.startsWith(nationalPrefix)) {
      newValue = newValue.slice(nationalPrefix.length);
    }

    this.$emit("update:phone", newValue);
  }

  indonesianPhone({ phone, code }: PhoneBookingV3) {
    return code === "+62" && phone.startsWith("62") ? phone.slice(2) : phone;
  }

  get accountIsForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  get validPhone() {
    if (isMaskedPhone(this.phone)) {
      return false;
    }

    const supportsValidIcon =
      this.isBookingV4 ||
      (/^(\+62|62)/.test(this.code) && !this.accountIsForeign);

    return (
      !this.customError &&
      this.code &&
      this.phone &&
      !this.error &&
      supportsValidIcon
    );
  }

  highlightSearch(text: string): string {
    const searchText = this.code + this.phone;

    if (!searchText) return text;

    return text?.replace(searchText, `<strong>${searchText}</strong>`);
  }

  isTooltipCheckGuide = false;
  toggleTooltipCheckGuide(event: any) {
    this.isTooltipCheckGuide = !this.isTooltipCheckGuide;

    const refs: any = this.$refs;
    refs?.["check-guide"]?.toggle(event);
  }

  onScroll() {
    this.$emit("scrolled");
  }

  get isBookingV4() {
    return this.bookingVersion === "v4";
  }
}
